/**
 * Simulate — the pure half (bata-ui#148 5.3, `docs/adr/6007`).
 *
 * A simulation is a scenario evaluation, not a run button (`bata-reqs` ADR 1002): the planner
 * sets parameters on a scenario branch and the platform evaluates ONE stage of a live run's
 * window under them (bata-platform ADR 6041). The live queue is fenced from everything a
 * simulation writes, so nothing here can reach a disposition, a pack or a write-back.
 *
 * Everything decided in the browser is decided here, on strings, and tested:
 *
 * - **the URL is the question** — `?runId=` (the base run, never defaulted), `?stage=`
 *   (`distribute` or `price`, never defaulted), `?scenario=` and `?simulation=`;
 * - **the levers** are the registry rows in force at the run's window end, filtered to the
 *   stage's section as the platform filters them (`STAGE_SECTIONS`) — `./levers.ts` (bata-ui#196
 *   §2.7) builds them, with the register's own controls;
 * - **the overlay** is the values the planner typed, trimmed, blanks dropped;
 * - **a saved configuration** contributes only its string entries inside the section — the
 *   platform's own recall rule — and the request's entries win;
 * - **polling** stops on a terminal state, and a state this build does not know is terminal
 *   too (a poll that never ends is worse than one that stops early and says why);
 * - **the comparison** is the platform's: its deltas, its declared polarity. This module maps
 *   a polarity onto `DeltaValue`'s vocabulary and computes no measure.
 */

import { ApiProblemError, callOperation } from '@/api';
import { compareSimulation, listSimulations } from '@/api/generated';
import type {
  CycleRunSummary, DigestRate, InSeasonRunDigest, MeasureDeltaView, ParameterKeyEntry, ParameterRow, ProposalLineDiff, RunSurface,
  ScopePair, SimulationComparisonView, SimulationView,
} from '@/api/generated';
import type { ChartSpec, Datum } from '@/charts';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportRequest, Polarity } from '@/export';
import { PERSON_NAME_DISPLAY, personExportCell } from '@/planning';

import type { ExportScope } from '../manifest';
import { RUN_ID_PARAM } from './scope';
import { DROPPED_BOUND_BY } from './dropped-door';
import { catalogueWords } from './export-drivers';

// ── the URL ──────────────────────────────────────────────────────────────────

/** The route. Reached from the Cockpit, the queue screens and — since bata-ui#166 W2-11 — the rail. */
export const SIMULATE_PATH = '/inseason/simulate';
/**
 * The base run's simulations — its own page since client rule 3 (2026-09-30, "one page, one
 * function"; ADR 6007's amendment). A static segment, so the router resolves it before
 * `/inseason/simulate/:simulationId` (`resolveRoute`: static segments first).
 */
export const SIMULATE_HISTORY_PATH = `${SIMULATE_PATH}/history`;
/** The segment under one simulation that proposes its parameters. */
export const PROPOSE_SEGMENT = 'propose';
export const STAGE_PARAM = 'stage';
export const SCENARIO_PARAM = 'scenario';
export const SIMULATION_PARAM = 'simulation';
/**
 * The simulation whose question the form is filled from while the base run is another one
 * (ADR 6203, «Rilancia sul run corrente»). Unlike `?simulation=` it names nothing launched from
 * THIS base, so the form shows no state for it and is not locked by it.
 */
export const FROM_PARAM = 'from';
/** "For example" (W2-7): the article, and for distribute the store, whose lines are compared. */
export const ARTICLE_PARAM = 'article';
export const STORE_PARAM = 'store';

/** The two stages a simulation can evaluate, as the URL spells them. */
export const STAGES = ['distribute', 'price'] as const;
export type SimulateStage = (typeof STAGES)[number];

/** The stage as the contract names it. */
export const CONTRACT_STAGE: Readonly<Record<SimulateStage, 'inseason.distribute' | 'inseason.price'>> = {
  distribute: 'inseason.distribute',
  price: 'inseason.price',
};

export interface SimulateScope {
  readonly runId: string | null;
  readonly stage: SimulateStage | null;
  readonly scenarioId: string | null;
  readonly simulationId: string | null;
  /** The simulation whose question fills the form on a switched base (`?from=`, ADR 6203). */
  readonly fromId: string | null;
  /** "For example" (W2-7): the article and store whose lines are compared. No default. */
  readonly article: string | null;
  readonly store: string | null;
}

function stated(params: URLSearchParams, name: string): string | null {
  const raw = params.get(name)?.trim() ?? '';
  return raw === '' ? null : raw;
}

/** Pure parse. No default for any of the four — not the newest run, not the distribute stage. */
export function readSimulateScope(search: string): SimulateScope {
  const params = new URLSearchParams(search);
  const stage = stated(params, STAGE_PARAM);
  return {
    runId: stated(params, RUN_ID_PARAM),
    stage: stage !== null && (STAGES as readonly string[]).includes(stage) ? (stage as SimulateStage) : null,
    scenarioId: stated(params, SCENARIO_PARAM),
    simulationId: stated(params, SIMULATION_PARAM),
    fromId: stated(params, FROM_PARAM),
    article: stated(params, ARTICLE_PARAM),
    store: stated(params, STORE_PARAM),
  };
}

/** The search string with `name` set, or removed on `null`. */
export function withParam(search: string, name: string, value: string | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (value === null) next.delete(name);
  else next.set(name, value);
  return next;
}

/** One simulation's results page. */
export function simulationPath(simulationId: string): string {
  return `${SIMULATE_PATH}/${encodeURIComponent(simulationId)}`;
}

/** One simulation's proposal page. */
export function simulationProposePath(simulationId: string): string {
  return `${simulationPath(simulationId)}/${PROPOSE_SEGMENT}`;
}

/**
 * The simulation a pathname names, or `null` — `/inseason/simulate/<id>` and
 * `/inseason/simulate/<id>/propose`. `history` is the history page, never a simulation id.
 */
export function readSimulationDrill(pathname: string): string | null {
  const base = SIMULATE_PATH.split('/').filter(Boolean);
  const segments = pathname.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return null;
  const rest = segments.slice(base.length);
  const tailOk = rest.length === 1 || (rest.length === 2 && rest[1] === PROPOSE_SEGMENT);
  if (!tailOk) return null;
  try {
    const id = decodeURIComponent(rest[0] ?? '').trim();
    return id === '' || id === 'history' ? null : id;
  } catch {
    return null;
  }
}

/**
 * The compose page asking one simulation again (owner ruling 2026-10-20): the query that opens
 * `/inseason/simulate` on the simulation's own question — its base run, its stage, its scenario —
 * with `?simulation=` naming it, so the composer fills its values from the stored overlay.
 *
 * `?simulation=` on the compose page is the simulation launched from it. While it is queued or
 * running the form is read-only; failed or expired, the form stays filled and editable; succeeded,
 * the composer opens its results page — which is where the retired `?simulation=` redirect used to
 * send an old link, so an old link still lands on the results when there are results to land on.
 */
export function composeAgainQuery(simulation: Pick<SimulationView, 'base_run_id' | 'scenario_id' | 'simulation_id' | 'stage'>): URLSearchParams {
  return new URLSearchParams({
    [RUN_ID_PARAM]: simulation.base_run_id,
    [STAGE_PARAM]: ownStage(simulation.stage),
    [SCENARIO_PARAM]: simulation.scenario_id,
    [SIMULATION_PARAM]: simulation.simulation_id,
  });
}

/** The stage a simulation names, in the URL's spelling. */
export function ownStage(stage: string): SimulateStage {
  return stage === CONTRACT_STAGE.price ? 'price' : 'distribute';
}

/** Whether a simulation answers the question the compose page's URL states: its run and its stage. */
export function belongsToCompose(simulation: Pick<SimulationView, 'base_run_id' | 'stage'>, runId: string, stage: SimulateStage): boolean {
  return simulation.base_run_id === runId && ownStage(simulation.stage) === stage;
}

/**
 * Why a simulation did not give an answer, as a sentence — never the platform's code (owner
 * ruling 2026-10-20: business words). The runner records `<code>:<detail>` for its own refusals
 * and the exception's type for a stage that raised; only the code reaches this read. A code this
 * build does not know reads as "stopped on an error it did not describe", never as the code.
 */
export const SIMULATION_FAILURE_KEY: Readonly<Record<string, string>> = {
  runner_lost: 'inseason.simulate.failure.runnerLost',
  job_sources_unavailable: 'inseason.simulate.failure.sourcesUnavailable',
  job_sources_unwired: 'inseason.simulate.failure.sourcesUnwired',
  job_unregistered: 'inseason.simulate.failure.unregistered',
  simulation_overlay_invalid: 'inseason.simulate.failure.overlayInvalid',
  module_gate_unreadable: 'inseason.simulate.failure.moduleGate',
};

/** The sentence a terminal simulation without an answer reads as; `null` while it can still answer. */
export function simulationFailureKey(state: string, failureCode: string | null | undefined): string | null {
  if (state === 'expired') return 'inseason.simulate.failure.expired';
  if (state !== 'failed') return null;
  if (failureCode === null || failureCode === undefined || failureCode === '') return 'inseason.simulate.failure.unstated';
  return SIMULATION_FAILURE_KEY[failureCode] ?? 'inseason.simulate.failure.unexplained';
}

/**
 * A launch the platform refused because of ONE value (owner ruling 2026-10-20): the problem names
 * the key as `parameterKey`, with its `reason` and, when it states one, the `range` it accepts. The
 * composer draws it on that lever's row, in words, and keeps every value typed.
 */
export interface FieldRefusal {
  readonly key: string;
  readonly reason: string;
  /** The bounds as the platform states them, in the stored unit (a share as a fraction); `null` when unstated. */
  readonly lower: string | null;
  readonly upper: string | null;
}

/** The refused value a problem names, or `null` when it names none. Pure. */
export function fieldRefusalOf(error: unknown): FieldRefusal | null {
  if (!(error instanceof ApiProblemError)) return null;
  const key = error.problem['parameterKey'];
  if (typeof key !== 'string' || key.trim() === '') return null;
  const bound = (value: unknown): string | null => (
    typeof value === 'number' && Number.isFinite(value) ? String(value)
      : typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  );
  const range = error.problem['range'] !== null && typeof error.problem['range'] === 'object'
    ? error.problem['range'] as Readonly<Record<string, unknown>>
    : {};
  const reason = error.problem['reason'];
  return { key, reason: typeof reason === 'string' ? reason : '', lower: bound(range['lower']), upper: bound(range['upper']) };
}

/** Which sentence a field refusal reads as: the range it accepts when stated, else the reason. */
export function fieldRefusalKey(refusal: FieldRefusal): string {
  if (refusal.lower !== null && refusal.upper !== null) return 'inseason.simulate.fieldRefusal.range';
  if (refusal.lower !== null) return 'inseason.simulate.fieldRefusal.floor';
  return 'inseason.simulate.fieldRefusal.reason';
}

/** Failed or expired: the question may be asked again («Modifica e rilancia»). */
export function mayAskAgain(state: string): boolean {
  return state === 'failed' || state === 'expired';
}

/** The query a Simulate entry opens with: the run it is over and the stage, nothing else. */
export function simulateQuery(runId: string | null, stage: SimulateStage): Record<string, string> {
  return runId === null ? { [STAGE_PARAM]: stage } : { [RUN_ID_PARAM]: runId, [STAGE_PARAM]: stage };
}

// ── the tunables ─────────────────────────────────────────────────────────────

/**
 * The registry sections each stage's overlay may name — bata-platform's `STAGE_SECTIONS`,
 * verbatim. A key outside them is read by nothing the stage runs, and the platform refuses it.
 */
export const STAGE_SECTIONS: Readonly<Record<SimulateStage, readonly string[]>> = {
  distribute: ['inseason.pull.', 'allocation.'],
  price: ['markdown.'],
};

/** The platform's bounds on an overlay (`MAX_OVERLAY_KEYS`, `MAX_OVERLAY_VALUE_LENGTH`). */
export const MAX_OVERLAY_KEYS = 32;
export const MAX_OVERLAY_VALUE_LENGTH = 128;

export function inSection(key: string, stage: SimulateStage): boolean {
  return STAGE_SECTIONS[stage].some((prefix) => key.startsWith(prefix));
}

/** A registry row's value as the registry publishes it, as a string; `null` when it holds none. */
export function rowValue(row: Pick<ParameterRow, 'numericValue' | 'textValue'>): string | null {
  if (row.textValue !== null && row.textValue !== undefined) return row.textValue;
  if (row.numericValue !== null && row.numericValue !== undefined) return String(row.numericValue);
  return null;
}

/**
 * The keys a planner may try in a simulation, as the catalogue says (W2-10, bata-platform ADR 6046):
 * `plannerTunable: true`. Solver knobs, model and scheme ids, statistical levels and data-acceptance
 * policies are not — the platform refuses them with `overlay_key_refused` / `not_planner_tunable`.
 * `null` when the catalogue publishes no such flag (a platform before v0.51): the section filter
 * alone then applies, as before, and the screen says the list is not narrowed.
 */
export function plannerTunableKeys(entries: readonly ParameterKeyEntry[] | undefined): ReadonlySet<string> | null {
  if (entries === undefined || !entries.some((entry) => entry.plannerTunable !== undefined)) return null;
  return new Set(entries.filter((entry) => entry.plannerTunable === true).map((entry) => entry.parameterKey));
}

/** The date a run's registry is read at: its window end (`generated_at`), as `YYYY-MM-DD`. */
export function runRegistryDate(generatedAt: string): string | null {
  const date = generatedAt.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

/** The overlay the planner typed: trimmed, blanks dropped, only keys of the stage's section. */
export function composeOverlay(
  typed: Readonly<Record<string, string>>, stage: SimulateStage,
): Readonly<Record<string, string>> {
  const overlay: Record<string, string> = {};
  for (const key of Object.keys(typed).sort()) {
    const value = typed[key]?.trim() ?? '';
    if (value !== '' && inSection(key, stage)) overlay[key] = value;
  }
  return overlay;
}

export type OverlayProblem = 'too_many_keys' | 'value_too_long' | null;

/** What the platform would refuse about an overlay's shape, checked before it is sent. */
export function overlayProblem(overlay: Readonly<Record<string, string>>): OverlayProblem {
  const values = Object.values(overlay);
  if (values.length > MAX_OVERLAY_KEYS) return 'too_many_keys';
  if (values.some((value) => value.length > MAX_OVERLAY_VALUE_LENGTH)) return 'value_too_long';
  return null;
}

/**
 * The entries a saved run configuration would seed the overlay with — the platform's recall
 * rule: string values under a key of the stage's section. Everything else it holds (the
 * markdown Run view's own URL parameters) is not a registry entry and is left where it is.
 */
export function recalledEntries(parameters: unknown, stage: SimulateStage): Readonly<Record<string, string>> {
  if (parameters === null || typeof parameters !== 'object' || Array.isArray(parameters)) return {};
  const recalled: Record<string, string> = {};
  for (const [key, value] of Object.entries(parameters as Record<string, unknown>)) {
    if (typeof value === 'string' && inSection(key, stage)) recalled[key] = value;
  }
  return recalled;
}

// ── the run's state ──────────────────────────────────────────────────────────

/** Job-run states the platform publishes for a simulation (`SimulationView.state`). */
export const SIMULATION_STATE_KEY: Readonly<Record<string, string>> = {
  queued: 'inseason.simulate.state.queued',
  running: 'inseason.simulate.state.running',
  succeeded: 'inseason.simulate.state.succeeded',
  failed: 'inseason.simulate.state.failed',
  expired: 'inseason.simulate.state.expired',
};

export const UNRECOGNISED_STATE_KEY = 'inseason.simulate.state.unrecognised';

export function simulationStateKey(state: string): string {
  return SIMULATION_STATE_KEY[state] ?? UNRECOGNISED_STATE_KEY;
}

/** Only these two are still moving. Anything else — an unknown word included — stops the poll. */
export function isInFlight(state: string): boolean {
  return state === 'queued' || state === 'running';
}

/** How often an in-flight simulation is read again. */
export const POLL_INTERVAL_MS = 5000;

// ── the comparison ───────────────────────────────────────────────────────────

/** The platform's declared polarity in `DeltaValue`'s words. An unknown word judges nothing. */
export function toPolarity(polarity: string): Polarity {
  if (polarity === 'higher_is_better') return 'higher-is-better';
  if (polarity === 'lower_is_better') return 'lower-is-better';
  return 'none';
}

/** Each headline measure the platform compares (`planning.replenishment.compare`). */
export const MEASURE_KEY: Readonly<Record<string, string>> = {
  'proposals.proposals': 'inseason.simulate.measure.proposals',
  'proposals.units': 'inseason.simulate.measure.units',
  'proposals.requested_units': 'inseason.simulate.measure.requestedUnits',
  'proposals.unmet_units': 'inseason.simulate.measure.unmetUnits',
  'proposals.fill_rate': 'inseason.simulate.measure.fillRate',
  'proposals.stores': 'inseason.simulate.measure.stores',
  'proposals.stores_with_nothing': 'inseason.simulate.measure.storesWithNothing',
  'proposals.expected_impact': 'inseason.simulate.measure.expectedImpact',
  'price_steps.price_steps': 'inseason.simulate.measure.priceSteps',
  'price_steps.expected_margin_recovery': 'inseason.simulate.measure.marginRecovery',
  'transfers.transfers': 'inseason.simulate.measure.transfers',
  'transfers.units': 'inseason.simulate.measure.transferUnits',
  'transfers.net_benefit': 'inseason.simulate.measure.netBenefit',
};

/** The catalogue key for a measure, or `null` — the caller shows the code verbatim. */
export function measureKey(measure: string): string | null {
  return MEASURE_KEY[measure] ?? null;
}

/** The measures that are a ratio: shown as a percentage, their delta in points. */
export const RATE_MEASURES: readonly string[] = ['proposals.fill_rate'];

export function isRate(row: Pick<MeasureDeltaView, 'measure'>): boolean {
  return RATE_MEASURES.includes(row.measure);
}

/** Why a measure has no delta — the platform's four reasons. */
export const ABSENT_REASON_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.simulation.compare.base_run_absent': 'inseason.simulate.absent.baseRun',
  'planning.inseason.simulation.compare.scenario_run_absent': 'inseason.simulate.absent.scenarioRun',
  'planning.inseason.simulation.compare.absent_on_one_side': 'inseason.simulate.absent.oneSide',
  'planning.inseason.simulation.compare.currency_on_one_side': 'inseason.simulate.absent.currency',
};

export function absentReasonKey(code: string | null | undefined): string {
  return (code === null || code === undefined ? undefined : ABSENT_REASON_KEY[code]) ?? 'inseason.simulate.absent.unstated';
}

// ── refusals ─────────────────────────────────────────────────────────────────

/** The simulation routes' own refusals, each a sentence in the catalogue. */
export const REFUSAL_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.simulation.base_run_not_found': 'inseason.simulate.refusal.baseRunNotFound',
  'planning.inseason.simulation.base_job_run_unavailable': 'inseason.simulate.refusal.baseJobRun',
  'planning.inseason.simulation.scenario_not_found': 'inseason.simulate.refusal.scenarioNotFound',
  'planning.inseason.simulation.configuration_not_found': 'inseason.simulate.refusal.configurationNotFound',
  'planning.inseason.simulation.overlay_empty': 'inseason.simulate.refusal.overlayEmpty',
  'planning.inseason.simulation.overlay_key_refused': 'inseason.simulate.refusal.overlayKey',
  'planning.inseason.simulation.capacity_exhausted': 'inseason.simulate.refusal.capacity',
  'planning.inseason.simulation.not_found': 'inseason.simulate.refusal.notFound',
  'planning.inseason.simulation.not_complete': 'inseason.simulate.refusal.notComplete',
  'planning.inseason.simulation.unavailable': 'inseason.simulate.refusal.unavailable',
  // W2-7: a price simulation's steps carry no store; a distribute one needs it.
  'planning.inseason.simulation.rows_filter_invalid': 'inseason.simulate.refusal.rowsFilterInvalid',
};

// ── saved configurations, per surface (bata-platform ADR 2002 amended, DEC-68) ─────

/**
 * The saved-configuration surface each stage recalls from — the platform's `STAGE_SURFACES`. The
 * distribute stage recalls the Simulate screen's own captures; the price stage the markdown Run
 * view's. A configuration of the other surface is refused at recall with 422
 * `configuration_surface_mismatch`, so the screen offers only the stage's own.
 */
export const STAGE_SURFACE: Readonly<Record<SimulateStage, RunSurface>> = {
  distribute: 'inseason_distribute',
  price: 'inseason_markdown',
};

/** The stages whose configurations this screen SAVES: only its own surface. */
export function savesConfigurations(stage: SimulateStage): boolean {
  return STAGE_SURFACE[stage] === 'inseason_distribute';
}

export const SURFACE_MISMATCH_CODE = 'planning.inseason.simulation.configuration_surface_mismatch';

/**
 * 422 `configuration_surface_mismatch`: the configuration's surface and the simulation's stage,
 * as the problem's members name them; `null` for any other error.
 */
export function surfaceMismatch(error: unknown): { readonly surface: string; readonly stage: string } | null {
  if (!(error instanceof ApiProblemError) || error.code !== SURFACE_MISMATCH_CODE) return null;
  const member = (name: string): string => {
    const value = error.problem[name];
    return typeof value === 'string' ? value : '';
  };
  return { surface: member('surface'), stage: member('stage') };
}

// ── the plan version the base run read (bata-platform ADR 6049, DEC-67/74) ──────

/** Why a run names no plan version. Only `not_read_by_run` is published today. */
const PLAN_VERSION_ABSENT_KEY: Readonly<Record<string, string>> = {
  not_read_by_run: 'inseason.simulate.planVersion.notReadByRun',
};

/**
 * What the base run says about the plan version it read: the id verbatim, the reason it has
 * none, or `unpublished` on a platform before 0.52. Never a version chosen for it — no default
 * branch is derived (DEC-74).
 */
export type PlanVersionStatement =
  | { readonly kind: 'read'; readonly planVersionId: string }
  | { readonly kind: 'absent'; readonly reasonKey: string; readonly code: string }
  | { readonly kind: 'unpublished' };

export function planVersionStatement(run: Pick<CycleRunSummary, 'plan_version_id' | 'plan_version_absent_reason'> | undefined): PlanVersionStatement {
  if (run === undefined) return { kind: 'unpublished' };
  const id = run.plan_version_id;
  if (typeof id === 'string' && id !== '') return { kind: 'read', planVersionId: id };
  const code = run.plan_version_absent_reason;
  if (typeof code === 'string' && code !== '') {
    return { kind: 'absent', reasonKey: PLAN_VERSION_ABSENT_KEY[code] ?? 'inseason.simulate.planVersion.otherReason', code };
  }
  return { kind: 'unpublished' };
}

// ── depth weighted by the units repriced (bata-platform ADR 6051) ─────────────

/** Why a digest's units-weighted depth is absent — the platform's two reasons. */
const DEPTH_ABSENT_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.digest.units_not_recorded': 'inseason.depthWeighted.unitsNotRecorded',
  'planning.inseason.digest.nothing_repriced': 'inseason.depthWeighted.nothingRepriced',
};

/**
 * A digest's `depth_units_weighted`: the ratio, or the catalogue key for why there is none. A
 * digest from a platform before 0.52 carries no field — `unpublished`, said so, never 0.
 */
export type DepthWeighted =
  | { readonly kind: 'value'; readonly value: number }
  | { readonly kind: 'absent'; readonly reasonKey: string };

export function depthWeighted(rate: DigestRate | undefined | null): DepthWeighted {
  if (rate === undefined || rate === null) return { kind: 'absent', reasonKey: 'inseason.depthWeighted.notPublished' };
  if (typeof rate.value === 'number') return { kind: 'value', value: rate.value };
  const code = rate.absent_reason ?? '';
  return { kind: 'absent', reasonKey: DEPTH_ABSENT_KEY[code] ?? 'inseason.depthWeighted.unstated' };
}

/** W2-9 (bata-platform ADR 6046): the scheduled cycle has priority over simulations. */
export const CYCLE_WINDOW_CODE = 'planning.inseason.simulation.cycle_window';

export type CycleWindowRefusal =
  /** Inside the window around a cycle tick: ask again after `tryAfter`. */
  | { readonly kind: 'tryAfter'; readonly cycle: string; readonly tryAfter: string }
  /** Cycle work is in flight: no instant can be promised (`tryAfter: null`). */
  | { readonly kind: 'cycleRunning'; readonly cycle: string };

export function cycleWindowRefusal(error: unknown): CycleWindowRefusal | null {
  if (!(error instanceof ApiProblemError) || error.problem.code !== CYCLE_WINDOW_CODE) return null;
  const cycle = typeof error.problem['cycle'] === 'string' ? error.problem['cycle'] : '';
  const tryAfter = error.problem['tryAfter'];
  return typeof tryAfter === 'string' && !Number.isNaN(Date.parse(tryAfter))
    ? { kind: 'tryAfter', cycle, tryAfter }
    : { kind: 'cycleRunning', cycle };
}

/** "HH:MM" of an instant, in the reader's language and zone — the refusal's "try after". */
export function clockTime(instant: string, language: string, timeZone?: string): string {
  return new Date(instant).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', ...(timeZone === undefined ? {} : { timeZone }) });
}

// ── "for example": one store × article, base beside scenario (W2-7) ──────────

/**
 * Whether the rows read can be asked: an article always, a store for distribute, and no store for
 * price (the platform answers 422 `rows_filter_invalid` otherwise).
 */
export function rowsQuery(
  stage: SimulateStage, article: string | null, store: string | null,
): { readonly article_key: string; readonly store_key?: string } | null {
  const a = article?.trim() ?? '';
  const s = store?.trim() ?? '';
  if (a === '') return null;
  if (stage === 'distribute') return s === '' ? null : { article_key: a, store_key: s };
  return { article_key: a };
}

/** A line's drivers, as `{code: contribution}`, for one side; `{}` when the side is absent. */
export function driverMap(side: ProposalLineDiff['base']): Readonly<Record<string, number>> {
  const map: Record<string, number> = {};
  for (const driver of side?.drivers ?? []) map[driver.code] = driver.contribution;
  return map;
}

/** The driver codes either side names, in first-seen order — base first. */
export function driverCodes(line: ProposalLineDiff): readonly string[] {
  return [...new Set([...(line.base?.drivers ?? []), ...(line.scenario?.drivers ?? [])].map((driver) => driver.code))];
}

// ── adopt at one scope (W2-8) ────────────────────────────────────────────────

/** The adoption's scope: the estate row (no pair), or the simulated run's market. */
export type AdoptScope = 'estate' | 'market';

export function adoptScope(choice: AdoptScope, country: string): readonly ScopePair[] {
  return choice === 'market' && country.trim() !== '' ? [{ dimension: 'country', value: country }] : [];
}

/** What a surviving exception means for the adopted value (`overridesAdopted`). */
export function survivingKey(overridesAdopted: boolean | null): string {
  if (overridesAdopted === true) return 'inseason.simulate.adopt.surviving.overrides';
  if (overridesAdopted === false) return 'inseason.simulate.adopt.surviving.lessSpecific';
  return 'inseason.simulate.adopt.surviving.precedence';
}

/** Why one overlay key was refused (`reason` on `overlay_key_refused`). */
export const OVERLAY_REASON_KEY: Readonly<Record<string, string>> = {
  key_unknown: 'inseason.simulate.overlayReason.unknown',
  outside_stage_section: 'inseason.simulate.overlayReason.outsideSection',
  not_in_force: 'inseason.simulate.overlayReason.notInForce',
  value_invalid: 'inseason.simulate.overlayReason.invalid',
  outside_band: 'inseason.simulate.overlayReason.outsideBand',
  // A text value outside the key's published `vocabulary` (bata-platform pull request 832).
  not_in_vocabulary: 'inseason.simulate.overlayReason.notInVocabulary',
  type_not_overlayable: 'inseason.simulate.overlayReason.notOverlayable',
  // W2-10: a key the catalogue marks as not a planner's lever (a solver knob, a model id…).
  not_planner_tunable: 'inseason.simulate.overlayReason.notPlannerTunable',
};

// ── the distribution chart ───────────────────────────────────────────────────

/** A chart carries at most seven categories here; past that the tail folds into one. */
export const MAX_CATEGORIES = 7;
export const OTHER_CATEGORY = '__other__';

export interface PairedDistribution {
  /** Category ids — group keys, then `OTHER_CATEGORY` when folded. */
  readonly keys: readonly string[];
  readonly base: readonly number[];
  readonly scenario: readonly number[];
  /** How many keys were folded into the last category. */
  readonly folded: number;
}

/**
 * Two runs' groups on one axis: every key either run holds, most units on either side first,
 * folded past {@link MAX_CATEGORIES}. A key one run does not hold is 0 on that side — it is a
 * group of lines that run did not write, which is a count of zero, not an absence of a figure.
 */
export function pairGroups(
  base: readonly { readonly key: string; readonly value: number }[],
  scenario: readonly { readonly key: string; readonly value: number }[],
  order: 'magnitude' | 'declared' = 'magnitude',
): PairedDistribution {
  const left = new Map(base.map((group) => [group.key, group.value]));
  const right = new Map(scenario.map((group) => [group.key, group.value]));
  const keys = [...new Set([...base.map((group) => group.key), ...scenario.map((group) => group.key)])];
  const sorted = order === 'declared'
    ? keys
    : keys.sort((a, b) => Math.max(left.get(b) ?? 0, right.get(b) ?? 0) - Math.max(left.get(a) ?? 0, right.get(a) ?? 0) || a.localeCompare(b));
  const fold = sorted.length > MAX_CATEGORIES;
  const shown = fold ? sorted.slice(0, MAX_CATEGORIES - 1) : sorted;
  const rest = fold ? sorted.slice(MAX_CATEGORIES - 1) : [];
  const sum = (side: Map<string, number>): number => rest.reduce((total, key) => total + (side.get(key) ?? 0), 0);
  return {
    keys: fold ? [...shown, OTHER_CATEGORY] : shown,
    base: [...shown.map((key) => left.get(key) ?? 0), ...(fold ? [sum(left)] : [])],
    scenario: [...shown.map((key) => right.get(key) ?? 0), ...(fold ? [sum(right)] : [])],
    folded: rest.length,
  };
}

/**
 * Units by the reason code that decided each line (distribute), or price steps by depth band (price).
 *
 * **The dropped door is not a bar** (bata-ui issue 435). Its marker lines carry 0 units by design
 * (`./dropped-door.ts`), so a units bar for `size_run_dropped` is 0 on both sides whatever the
 * parameters, and reads as «no store was dropped». It is left out here and stated beside the chart
 * in stores and requested pairs ({@link droppedDoors}).
 */
export function stageDistribution(
  stage: SimulateStage, base: InSeasonRunDigest, scenario: InSeasonRunDigest,
): PairedDistribution {
  if (stage === 'distribute') {
    const groups = (digest: InSeasonRunDigest): { key: string; value: number }[] =>
      digest.proposals.by_reason_code
        .filter((group) => group.key !== DROPPED_BOUND_BY)
        .map((group) => ({ key: group.key, value: group.units }));
    return pairGroups(groups(base), groups(scenario));
  }
  // Bands in depth order, shallowest first, as the digest lists them — an ordinal scale.
  const bands = (digest: InSeasonRunDigest): { key: string; value: number }[] =>
    digest.price_steps.by_depth_band.map((band) => ({ key: `${band.lower_points}`, value: band.price_steps }));
  const merged = [...new Set([...bands(base), ...bands(scenario)].map((band) => Number(band.key)))].sort((a, b) => a - b);
  const left = new Map(bands(base).map((band) => [band.key, band.value]));
  const right = new Map(bands(scenario).map((band) => [band.key, band.value]));
  return pairGroups(
    merged.map((lower) => ({ key: String(lower), value: left.get(String(lower)) ?? 0 })),
    merged.map((lower) => ({ key: String(lower), value: right.get(String(lower)) ?? 0 })),
    'declared',
  );
}

/** One run's dropped doors: stores left out for an incomplete size run, and the pairs they asked for. */
export type DroppedDoors =
  | { readonly kind: 'value'; readonly doors: number; readonly requestedUnits: number }
  | { readonly kind: 'absent' };

/**
 * The run's dropped doors from its digest (bata-platform ADR 6183): the stores whose only lines are
 * dropped-door markers, and the pairs those markers asked for. `absent` for a digest written before
 * the platform counted them, never 0: an uncounted run has not been shown to drop nothing.
 */
export function droppedDoors(digest: InSeasonRunDigest): DroppedDoors {
  const { dropped_doors: doors, dropped_door_requested_units: requestedUnits } = digest.proposals;
  if (doors === undefined || doors === null || requestedUnits === undefined || requestedUnits === null) {
    return { kind: 'absent' };
  }
  return { kind: 'value', doors, requestedUnits };
}

/**
 * The paired bar: base beside scenario, one pair per category, one unit. `null` when neither
 * run holds a group — the caller states that instead of drawing an empty frame.
 */
export function distributionSpec(
  stage: SimulateStage, pair: PairedDistribution, label: (key: string) => string,
): ChartSpec | null {
  if (pair.keys.length === 0) return null;
  return {
    form: 'paired-bar',
    orientation: 'horizontal',
    actionTitleKey: stage === 'distribute' ? 'inseason.simulate.chart.reasonTitle' : 'inseason.simulate.chart.depthTitle',
    actionTitleValues: { groups: pair.keys.length + Math.max(0, pair.folded - 1) },
    perimeterKey: 'inseason.simulate.chart.perimeter',
    valueAxis: { unitKey: stage === 'distribute' ? 'inseason.simulate.unit.units' : 'inseason.simulate.unit.priceSteps', zeroBased: true },
    categoryAxis: {
      kind: 'ordinal',
      labelKey: stage === 'distribute' ? 'inseason.simulate.chart.reasonAxis' : 'inseason.simulate.chart.depthAxis',
      order: pair.keys.map(label),
    },
    series: [
      { id: 'base', nameKey: 'inseason.simulate.chart.base', data: pair.base as readonly Datum[], distinguisher: 'pattern-solid', slot: 1 },
      { id: 'scenario', nameKey: 'inseason.simulate.chart.scenario', data: pair.scenario as readonly Datum[], distinguisher: 'pattern-hatch', slot: 2 },
    ],
  };
}

// ── the export ───────────────────────────────────────────────────────────────

const PLATFORM_VERSION = buildVersion();

/** The comparison table as a CSV: one row per measure (and currency), both sides and the delta. */
export function comparisonRequest(view: SimulationComparisonView | null, rows?: readonly MeasureDeltaView[]): ExportRequest {
  const measures = rows ?? view?.measures ?? [];
  const number = (value: number | null | undefined): ExportCell =>
    value === null || value === undefined ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value };
  const cells: ExportCell[][] = measures.map((row) => [
    { kind: 'label', value: row.measure, code: row.measure },
    row.currency_code === null || row.currency_code === undefined
      ? { kind: 'missing', state: 'not-ranged' }
      : { kind: 'text', value: row.currency_code },
    { kind: 'label', value: row.polarity, code: row.polarity },
    number(row.base),
    number(row.scenario),
    number(row.delta),
    row.absent_reason === null || row.absent_reason === undefined
      ? { kind: 'missing', state: 'not-ranged' }
      : { kind: 'label', value: row.absent_reason, code: row.absent_reason },
  ]);
  return {
    id: 'inseason.simulate.table',
    titleKey: 'inseason.simulate.title',
    columns: [
      // Codes in the screen's words in the workbook (usability audit E2); the CSV keeps them.
      { key: 'measure', headerKey: 'inseason.simulate.column.measure', descriptionKey: 'inseason.columnGuide.simulate.measure', kind: 'label', display: catalogueWords(measureKey) },
      { key: 'currency', headerKey: 'inseason.simulate.column.currency', descriptionKey: 'inseason.columnGuide.simulate.currency', kind: 'text' },
      { key: 'polarity', headerKey: 'inseason.simulate.column.polarity', descriptionKey: 'inseason.columnGuide.simulate.polarity', kind: 'label' },
      { key: 'base', headerKey: 'inseason.simulate.column.base', descriptionKey: 'inseason.columnGuide.simulate.base', kind: 'number' },
      { key: 'scenario', headerKey: 'inseason.simulate.column.scenario', descriptionKey: 'inseason.columnGuide.simulate.scenario', kind: 'number' },
      { key: 'delta', headerKey: 'inseason.simulate.column.delta', descriptionKey: 'inseason.columnGuide.simulate.delta', kind: 'number' },
      {
        key: 'absent_reason', headerKey: 'inseason.simulate.column.absentReason', descriptionKey: 'inseason.columnGuide.simulate.absentReason', kind: 'label',
        display: catalogueWords((code) => { const key = absentReasonKey(code); return key === 'inseason.simulate.absent.unstated' ? null : key; }),
      },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters: view === null ? [] : [
        { field: 'simulation_id', operator: '=', value: view.simulation.simulation_id },
        { field: 'base_run_id', operator: '=', value: view.simulation.base_run_id },
        { field: 'scenario_id', operator: '=', value: view.simulation.scenario_id },
      ],
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'simulation_delta',
        definition: 'The scenario run minus the base run, as the platform computes it from the two stored run digests (bata-platform ADR 6041). Polarity is the platform’s declaration per measure; an absent side has no delta and carries its reason.',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: ['measure', 'currency', 'polarity', 'base', 'scenario', 'delta', 'absent_reason'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The results route's registered export: the comparison its path names (or an old `?simulation=`). */
export async function simulationCompareExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const path = scope?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const simulationId = readSimulationDrill(path) ?? readSimulateScope(search).simulationId;
  if (simulationId === null) return comparisonRequest(null);
  const view = await callOperation(compareSimulation, { path: { simulation_id: simulationId } });
  return comparisonRequest(view);
}

/**
 * The history route's registered export — the base run's simulations as the page lists them:
 * the run on the URL, narrowed to the URL's stage where it names one. No run, no read.
 */
export async function simulationListExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const { runId, stage } = readSimulateScope(search);
  const items = runId === null
    ? []
    : simulationsOfStage((await callOperation(listSimulations, { query: { run_id: runId } })).items, stage);
  return simulationListRequest(items, runId, stage);
}

/** The simulations of one stage, or all of them when the URL names none. */
export function simulationsOfStage(
  items: readonly SimulationView[], stage: SimulateStage | null,
): readonly SimulationView[] {
  return stage === null ? items : items.filter((row) => row.stage === CONTRACT_STAGE[stage]);
}

/**
 * What became of a simulation's adoption (bata-platform ADR 6199, owner ruling 2026-10-20):
 * `adopted` once an approval was ever recorded on its change set (`adopted_at`) — it stays adopted
 * when that set is later superseded or replaced —, `proposed` once `…/adopt` proposed it
 * (`adoption_change_id`) and nobody approved it, including a proposal withdrawn or rejected since,
 * `none` while it was never taken to a proposal.
 */
export type SimulationAdoption = 'none' | 'proposed' | 'adopted';

export function simulationAdoption(row: Pick<SimulationView, 'adoption_change_id' | 'adopted_at'>): SimulationAdoption {
  if (row.adopted_at !== null && row.adopted_at !== undefined) return 'adopted';
  return row.adoption_change_id !== null && row.adoption_change_id !== undefined ? 'proposed' : 'none';
}

/** The words for each, literal keys so the coverage check sees all three. */
export const ADOPTION_KEY: Readonly<Record<SimulationAdoption, string>> = {
  none: 'inseason.simulateHistory.adoption.none',
  proposed: 'inseason.simulateHistory.adoption.proposed',
  adopted: 'inseason.simulateHistory.adoption.adopted',
};

/** The adoptions «Taken to a proposal» counts: every one proposed, adopted since or not. */
export const TAKEN_TO_PROPOSAL: readonly SimulationAdoption[] = ['proposed', 'adopted'];

const adoptionWords = (code: string): string | null => (code in ADOPTION_KEY ? ADOPTION_KEY[code as SimulationAdoption] : null);

/** The history table as a CSV — the rows given, in the order given. */
export function simulationListRequest(
  items: readonly SimulationView[], runId: string | null, stage: SimulateStage | null,
): ExportRequest {
  const cells: ExportCell[][] = items.map((row) => {
    const adoption = simulationAdoption(row);
    return [
      { kind: 'text', value: row.simulation_id },
      { kind: 'text', value: row.requested_at },
      { kind: 'label', value: row.stage, code: row.stage },
      { kind: 'label', value: row.state, code: row.state },
      { kind: 'number', raw: Object.keys(row.overlay).length },
      // Who asked, by name (bata-platform ADR 6198); the principal in its own column.
      personExportCell(row.requested_by, row.requested_by_name, row.requested_by_name_absent_reason),
      { kind: 'text', value: row.requested_by },
      { kind: 'label', value: adoption, code: adoption },
      row.adopted_at === null || row.adopted_at === undefined ? { kind: 'missing', state: 'not-ranged' } : { kind: 'text', value: row.adopted_at },
    ];
  });
  const filters = [
    runId === null
      ? { field: 'run_id', operator: 'unset', value: '' }
      : { field: 'run_id', operator: 'equals', value: runId },
    stage === null
      ? { field: 'stage', operator: 'unset', value: '' }
      : { field: 'stage', operator: 'equals', value: CONTRACT_STAGE[stage] },
  ];
  return {
    id: 'inseason.simulate.history',
    titleKey: 'inseason.simulateHistory.title',
    columns: [
      { key: 'simulation_id', headerKey: 'inseason.simulate.column.simulation', descriptionKey: 'inseason.columnGuide.simulate.simulationId', kind: 'text' },
      { key: 'requested_at', headerKey: 'inseason.simulate.column.requestedAt', descriptionKey: 'inseason.columnGuide.simulate.requestedAt', kind: 'text' },
      { key: 'stage', headerKey: 'inseason.simulate.column.stage', descriptionKey: 'inseason.columnGuide.simulate.stage', kind: 'label' },
      {
        key: 'state', headerKey: 'inseason.simulate.column.state', descriptionKey: 'inseason.columnGuide.simulate.state', kind: 'label',
        display: catalogueWords((code) => (simulationStateKey(code) === UNRECOGNISED_STATE_KEY ? null : simulationStateKey(code))),
      },
      { key: 'overlay_keys', headerKey: 'inseason.simulate.column.keys', descriptionKey: 'inseason.columnGuide.simulate.overlayKeys', kind: 'number' },
      { key: 'requested_by_name', headerKey: 'inseason.simulateHistory.column.by', descriptionKey: 'inseason.columnGuide.simulate.requestedByName', kind: 'label', display: PERSON_NAME_DISPLAY },
      { key: 'requested_by', headerKey: 'inseason.simulateHistory.column.byId', descriptionKey: 'inseason.columnGuide.simulate.requestedBy', kind: 'text' },
      { key: 'adoption', headerKey: 'inseason.simulateHistory.column.adoption', descriptionKey: 'inseason.columnGuide.simulate.adoption', kind: 'label', display: catalogueWords(adoptionWords) },
      {
        // Never adopted is the answer, not the assortment state: the cell reads «Not adopted» (ADR 9041 amendment 2026-10-21).
        key: 'adopted_at', headerKey: 'inseason.simulateHistory.column.adoptedAt', descriptionKey: 'inseason.columnGuide.simulate.adoptedAt', kind: 'text',
        absent: { notRanged: 'reason', reasonKeys: { 'not-ranged': 'inseason.simulateHistory.absent.notAdopted' } },
      },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'simulation',
        definition: 'One scenario-branch evaluation of a stage of the base run under a parameter overlay (bata-platform ADR 6041): when it was requested, the stage, its state, how many registry keys the overlay sets, who asked for it (by profile name, ADR 6198) and what became of its adoption (ADR 6199: proposed once its overlay was proposed as a change set, adopted once that set was ever approved).',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [{ key: 'requested_at', direction: 'desc' }],
      visibleColumns: ['simulation_id', 'requested_at', 'stage', 'state', 'overlay_keys', 'requested_by_name', 'requested_by', 'adoption', 'adopted_at'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
